/**
 * Client for the local LOD/SOG build service (`tools/lod-helper.mjs`).
 *
 * The browser's WebGPU device is capped at a 128 MB buffer, which multi-million
 * splat decimation blows through. When the helper is running on the machine's
 * real GPU the editor ships the splat there as PLY, gets the finished bundle
 * back, and never touches the in-browser builder. `?lodHelper=http://host:port`
 * overrides the endpoint on localhost only (see dev-host.ts).
 */

import { MemoryFileSystem } from '@playcanvas/splat-transform';

import type { Events } from '../events';
import { writeSplatFile } from '../splat-serialize';
import type { Splat } from '../splat';
import { devOnlyBase } from './dev-host';

const params = new URLSearchParams(window.location.search);
const HELPER = devOnlyBase(params, 'lodHelper', 'http://localhost:8087');

// LOD stays in the splat's local space: scene.json carries the entity
// transform and the viewer applies it once on load.
const SERIALIZE_SETTINGS = { keepWorldTransform: true };

/** Poll /health at boot and every 30 s; fires 'lodHelper.status' on change. */
export const startHelperMonitor = (events: Events) => {
    let online: boolean | null = null;
    const probe = async () => {
        const ok = await lodHelperAvailable();
        if (ok !== online) {
            online = ok;
            events.fire('lodHelper.status', ok);
        }
        return ok;
    };
    events.function('lodHelper.online', () => online === true);
    void probe();
    window.setInterval(() => {
        void probe();
    }, 30000);
};

export const lodHelperAvailable = async (): Promise<boolean> => {
    try {
        const ctrl = new AbortController();
        const t = window.setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch(`${HELPER}/health`, { signal: ctrl.signal });
        window.clearTimeout(t);
        return res.ok;
    } catch {
        return false;
    }
};

const b64ToBytes = (b64: string): Uint8Array => {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
};

/** The splat as it stands (deletions applied, edits baked), as PLY bytes. */
export const serializeSplatToPly = async (splat: Splat): Promise<Uint8Array> => {
    const fs = new MemoryFileSystem();
    await writeSplatFile([splat], SERIALIZE_SETTINGS, 'ply', 'in.ply', {}, fs);
    const ply = fs.results.get('in.ply');
    if (!ply) throw new Error('PLY serialization produced no bytes');
    return ply;
};

export const buildSogViaHelper = async (splat: Splat): Promise<Uint8Array> => {
    const ply = await serializeSplatToPly(splat);
    const res = await fetch(`${HELPER}/build-sog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: ply as BodyInit
    });
    if (!res.ok) throw new Error(`local GPU SOG build failed (${res.status})`);
    const data = await res.json() as { ok: boolean; sog?: string; error?: string };
    if (!data.ok || !data.sog) throw new Error(data.error || 'local GPU SOG build returned no bytes');
    return b64ToBytes(data.sog);
};

export const buildLodViaHelper = async (
    splat: Splat,
    opts: { onProgress?: (fraction: number, label: string) => void } = {}
): Promise<Map<string, Uint8Array>> => {
    const progress = opts.onProgress ?? (() => {});
    progress(0.05, 'Serialising splat to PLY…');
    const payload = await serializeSplatToPly(splat);
    progress(0.2, 'Sending to local GPU builder…');
    const res = await fetch(`${HELPER}/build-lod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: payload as BodyInit
    });
    if (!res.ok) {
        let detail = '';
        try {
            const j = await res.json() as { error?: string; log?: string };
            detail = j.error || j.log || '';
        } catch { /* ignore */ }
        throw new Error(`local GPU builder failed (${res.status})${detail ? `: ${String(detail).slice(-800)}` : ''}`);
    }
    progress(0.8, 'Receiving LOD…');
    const data = await res.json() as { ok: boolean; files?: Record<string, string>; error?: string; log?: string };
    if (!data.ok || !data.files) {
        const log = data.log ? ` — ${String(data.log).slice(-800)}` : '';
        throw new Error((data.error || 'local GPU builder returned no files') + log);
    }
    const files = new Map<string, Uint8Array>();
    for (const [name, b64] of Object.entries(data.files)) files.set(name, b64ToBytes(b64));
    progress(1.0, 'LOD ready (local GPU)');
    return files;
};
